import React, { useState, useEffect } from 'react';
import { Users, Clock, MapPin, ChevronRight, Heart, CheckCircle, Loader2, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import Header from '../Header';
import Footer from '../Footer';
import { API_URL } from '../../lib/api';

const JoinTeamPage = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showApplication, setShowApplication] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formConfig, setFormConfig] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferred_roles: [],
    experience_with_children: '',
    teaching_story: '',
    why_volunteer: '',
    skills: [],
    availability: ''
  });

  useEffect(() => {
    fetchRoles();
    fetchFormConfig();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await fetch(`${API_URL}/api/volunteer-roles`);
      if (response.ok) {
        const data = await response.json();
        setRoles(data);
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
    setLoading(false);
  };

  const fetchFormConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/volunteer/form-config`);
      if (response.ok) {
        setFormConfig(await response.json());
      }
    } catch (err) {
      console.error('Error fetching form config:', err);
    }
  };

  const handleApply = (role) => {
    setSelectedRole(role);
    setFormData(prev => ({
      ...prev,
      preferred_roles: [role.role_id]
    }));
    setShowApplication(true);
    // Scroll to application form
    setTimeout(() => {
      document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.why_volunteer) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/volunteer/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Application submitted! We\'ll be in touch soon.');
        setShowApplication(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          preferred_roles: [],
          experience_with_children: '',
          teaching_story: '',
          why_volunteer: '',
          skills: [],
          availability: ''
        });
      } else {
        toast.error('Failed to submit application. Please try again.');
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  const skillOptions = formConfig?.skill_options || [
    'Teaching', 'Writing', 'Illustration', 'Event Planning', 'Social Media',
    'Photography', 'Video Production', 'Music', 'Administration', 'Fundraising'
  ];

  const enabledFields = formConfig?.fields?.filter(f => f.enabled) || [];

  const renderFormField = (field) => {
    if (field.type === 'skills') {
      return (
        <div key={field.key}>
          <label className="block text-sm font-medium text-slate-700 mb-2">{field.label}{field.required && ' *'}</label>
          <div className="flex flex-wrap gap-2">
            {skillOptions.map((skill) => (
              <button
                key={skill}
                type="button"
                onClick={() => {
                  const skills = formData.skills.includes(skill)
                    ? formData.skills.filter(s => s !== skill)
                    : [...formData.skills, skill];
                  setFormData({...formData, skills});
                }}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  formData.skills.includes(skill)
                    ? 'bg-emerald-500 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {skill}
              </button>
            ))}
          </div>
        </div>
      );
    }
    
    if (field.type === 'textarea') {
      return (
        <div key={field.key}>
          <label className="block text-sm font-medium text-slate-700 mb-2">{field.label}{field.required && ' *'}</label>
          <Textarea
            value={formData[field.key] || ''}
            onChange={(e) => setFormData({...formData, [field.key]: e.target.value})}
            placeholder={field.placeholder || ''}
            rows={field.key === 'why_volunteer' ? 4 : 3}
            required={field.required}
          />
        </div>
      );
    }
    
    return (
      <div key={field.key}>
        <label className="block text-sm font-medium text-slate-700 mb-2">{field.label}{field.required && ' *'}</label>
        <Input
          type={field.type || 'text'}
          value={formData[field.key] || ''}
          onChange={(e) => setFormData({...formData, [field.key]: e.target.value})}
          placeholder={field.placeholder || ''}
          required={field.required}
        />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-emerald-50 to-sky-50">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="max-w-4xl mx-auto px-4 text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-600 px-4 py-2 rounded-full mb-6">
            <Users className="w-4 h-4" />
            <span className="text-sm font-medium">Join Our Team</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl font-serif font-bold text-slate-800 mb-4">
            Volunteer With
            <span className="text-emerald-600"> Creassion</span>
          </h1>
          
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {formConfig?.form_description || 'Use your gifts and talents to help children discover the love of God through engaging stories and resources. Every volunteer makes a difference.'}
          </p>
        </section>

        {/* Volunteer Roles */}
        <section className="max-w-5xl mx-auto px-4 mb-16">
          <h2 className="text-2xl font-serif font-bold text-slate-800 mb-6 text-center">
            Current Opportunities
          </h2>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
          ) : roles.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No volunteer positions available at the moment.</p>
              <p className="text-sm text-slate-400 mt-2">Check back soon or contact us to express your interest!</p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              {roles.map((role) => (
                <div 
                  key={role.role_id} 
                  className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg transition-all overflow-hidden"
                >
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-slate-800 mb-2">{role.title}</h3>
                    <p className="text-slate-600 mb-4">{role.description}</p>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-slate-500 mb-4">
                      {role.time_commitment && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{role.time_commitment}</span>
                        </div>
                      )}
                      {role.location && (
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          <span>{role.location}</span>
                        </div>
                      )}
                    </div>

                    {role.responsibilities && role.responsibilities.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-slate-700 mb-2">Key Responsibilities:</h4>
                        <ul className="space-y-1">
                          {role.responsibilities.slice(0, 3).map((resp, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                              <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                              <span>{resp}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {role.requirements && role.requirements.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-slate-700 mb-2">Requirements:</h4>
                        <ul className="space-y-1">
                          {role.requirements.slice(0, 3).map((req, i) => (
                            <li key={i} className="text-sm text-slate-500">• {req}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  
                  <div className="px-6 py-4 bg-slate-50 border-t border-slate-100">
                    <Button 
                      onClick={() => handleApply(role)}
                      className="w-full bg-emerald-500 hover:bg-emerald-600 rounded-xl"
                    >
                      Apply for this Role
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Application Form */}
        {showApplication && (
          <section id="application-form" className="max-w-2xl mx-auto px-4 mb-16">
            <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Send className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Volunteer Application</h2>
                  {selectedRole && (
                    <p className="text-sm text-emerald-600">Applying for: {selectedRole.title}</p>
                  )}
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name and Email are always first, in a grid if both present */}
                {(() => {
                  const nameField = enabledFields.find(f => f.key === 'name');
                  const emailField = enabledFields.find(f => f.key === 'email');
                  const otherFields = enabledFields.filter(f => f.key !== 'name' && f.key !== 'email');
                  
                  return (
                    <>
                      {(nameField || emailField) && (
                        <div className="grid gap-4 md:grid-cols-2">
                          {nameField && renderFormField(nameField)}
                          {emailField && renderFormField(emailField)}
                        </div>
                      )}
                      {otherFields.map(field => renderFormField(field))}
                    </>
                  );
                })()}

                <div className="flex gap-4 pt-4">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 h-12 rounded-xl"
                  >
                    {submitting ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Send className="w-5 h-5 mr-2" />
                    )}
                    {submitting ? 'Submitting...' : 'Submit Application'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowApplication(false)}
                    className="rounded-xl"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </section>
        )}

        {/* Why Volunteer Section */}
        <section className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-coral-500 to-coral-600 rounded-3xl p-8 lg:p-12 text-white">
            <div className="flex items-center gap-3 mb-6">
              <Heart className="w-8 h-8" />
              <h2 className="text-2xl font-serif font-bold">Why Volunteer With Us?</h2>
            </div>
            
            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <h3 className="font-semibold mb-2">Make a Real Impact</h3>
                <p className="text-coral-100 text-sm">
                  Directly influence children's spiritual growth and help shape the next generation of faith leaders.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Flexible Commitment</h3>
                <p className="text-coral-100 text-sm">
                  We offer various roles with different time requirements to fit your schedule and availability.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Grow Your Skills</h3>
                <p className="text-coral-100 text-sm">
                  Develop new abilities while using your existing talents in a supportive, faith-based environment.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Join a Community</h3>
                <p className="text-coral-100 text-sm">
                  Connect with like-minded believers who share your passion for nurturing children's faith.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default JoinTeamPage;
